import { MeasureData } from "../interfaces";
import {
    BODY_FAT_RANGE_PERCENT,
    calculateMuscleMass,
    resolveAgeYears,
    WEIGHT_RANGE_KG,
} from "./calculate-muscle";

export interface CompositionPoint {
    date: Date;
    fatPercent: number;
    fatKg: number;
    /** Everything that is not fat. Needs only weight and body fat. */
    leanKg: number;
    /**
     * The split of lean into skeletal muscle and the rest. It needs the
     * client's age and the scan's height as well, so it is absent for a
     * client created with neither an age nor a birth date — and those
     * surfaces then show Fat and Lean rather than a muscle figure of 0.
     */
    musclePercent?: number;
    otherPercent?: number;
    muscleKg?: number;
    otherKg?: number;
}

export interface CompositionSeries {
    /** Chronological, oldest first. */
    points: CompositionPoint[];
    /** Last element, or null when there is nothing to show. */
    latest: CompositionPoint | null;
}

export interface CompositionClient {
    gender: "male" | "female";
    age?: number;
    birthDate?: Date;
}

const inRange = (value: unknown, range: { min: number; max: number }): value is number =>
    typeof value === "number" && Number.isFinite(value) && value >= range.min && value <= range.max;

/**
 * Fat and lean mass from a weight and a body fat. Null when either is missing
 * or out of range. Unlike the muscle split, this needs nothing else about the
 * client.
 */
export const fatAndLean = (
    weightKg: number | undefined,
    bodyFatPercent: number | undefined
): { fatKg: number; fatPercent: number; leanKg: number } | null => {
    if (!inRange(weightKg, WEIGHT_RANGE_KG)) return null;
    if (!inRange(bodyFatPercent, BODY_FAT_RANGE_PERCENT)) return null;
    const fatKg = weightKg * (bodyFatPercent / 100);
    return { fatKg, fatPercent: bodyFatPercent, leanKg: weightKg - fatKg };
};

/**
 * Turns saved measurements into a body composition series.
 *
 * Weight is not always present — the API saves it from the scan's weight
 * resource, and scans started without one (ScanV2 made it optional from May
 * 2026; see lib/scan/weight.ts) carry none — and body fat
 * is only there once loadModels has recomputed it. Measurements missing either
 * are dropped rather than guessed at. The muscle split is left off a point,
 * not the point dropped, when age or height is what is missing.
 */
export const buildCompositionSeries = (
    measurements: MeasureData[],
    clientData: CompositionClient
): CompositionSeries => {
    const ageYears = resolveAgeYears(clientData.age, clientData.birthDate);
    const gender = clientData.gender === "female" ? "female" : "male";

    const points = [...measurements]
        .sort(
            (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        )
        .map((m): CompositionPoint | null => {
            const base = fatAndLean(m.measurements.weight, m.measurements.body_fat);
            if (!base) return null;

            const split = calculateMuscleMass({
                weightKg: m.measurements.weight ?? NaN,
                bodyFatPercent: m.measurements.body_fat ?? NaN,
                gender,
                ageYears: ageYears ?? NaN,
                heightCm: m.measurements.height,
            });

            return {
                date: new Date(m.createdAt),
                ...base,
                ...(split
                    ? {
                          musclePercent: split.musclePercent,
                          otherPercent: split.otherPercent,
                          muscleKg: split.muscleMassKg,
                          otherKg: split.otherKg,
                      }
                    : {}),
            };
        })
        .filter((p): p is CompositionPoint => p !== null);

    return { points, latest: points.length ? points[points.length - 1] : null };
};
